import React, { useEffect, useRef } from 'react';
import {
    Row,
    Col,
    Card,
    Button,
    Badge,
    ListGroup,
    Alert,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { describeProgramWindow } from '@/shared/utils/deadline';
import { inProgramDisplayOrder } from '@/shared/utils/programOrder';
import { isSwitchedOff } from '@/shared/utils/programInfo';
import ProgramComingSoon from '@/components/common/ProgramComingSoon';
import './RankedPreferences.css';
import { badgeTextFor } from '@/shared/utils/badgeText';

export const PROGRAMS = inProgramDisplayOrder([
    {
        key: 'si',
        label: 'SI',
        full: 'Supplemental Instruction',
        bg: 'success',
        description:
            'Peer-led study sessions for historically difficult courses. A trained student leads regular, optional group sessions open to everyone in the course - great for high-enrollment or high-DFW classes.',
    },
    {
        key: 'ta',
        label: 'TA',
        full: 'Teaching Assistant',
        bg: 'primary',
        description:
            'Direct course support from a student you name. Assists with course operations (e.g., prep, grading support, office hours). Best when you already have a specific student in mind.',
    },
    {
        key: 'la',
        label: 'LA',
        full: 'Learning Assistant',
        bg: '',
        badgeClass: 'badge-la',
        description:
            'The LA Program aims to improve the learning of all students. Students in courses supported by undergraduate LAs learn more than those in non-supported courses. Additionally, LAs improve their understanding of the course content, and many become interested in future careers as teachers and educators. The LA program also supports innovations in the college classroom and faculty reflection and improvement of their teaching practices.',
    },
]);

// Single source of truth for "is Program Selection complete" and, on a
// blocked advance, the one error to report — shared by CreateApplication.js
// and ApplicationDetailPage.js (which renders its own card grid rather than
// this component, but toggles the same selectedPrograms array) so both
// wizards report the same rule the same way. There's no per-program field
// to point to (it's a derived check over the whole array), so the id names
// the group container instead — see the group wrapper below. idPrefix must
// match whatever the caller's own group container id uses (this component
// always uses 'ca', ApplicationDetailPage.js's own hand-rolled card grid
// uses 'adp'), same reasoning as CreateApplicationSection6's idPrefix.
export function getProgramSelectionErrors(selectedPrograms, idPrefix = 'ca') {
    if (selectedPrograms.length === 0) {
        return [
            {
                id: `${idPrefix}-program-selection-group`,
                label: 'Program selection',
            },
        ];
    }
    return [];
}

// Section 2: Program Selection and Preferences
function CreateApplicationSection2({
    selectedPrograms,
    toggleProgram,
    moveProgram,
    eligiblePrograms,
    unavailableReasons = {},
    currentTerm,
    hasTA,
    errorIds = new Set(),
}) {
    const groupInvalid = errorIds.has('ca-program-selection-group');
    const now = new Date();

    // Moving a program to either end disables the arrow just pressed, which
    // drops keyboard focus to the page, so focus moves to its other arrow
    const refocusId = useRef(null);
    useEffect(() => {
        if (!refocusId.current) return;
        document.getElementById(refocusId.current)?.focus();
        refocusId.current = null;
    }, [selectedPrograms]);
    const moveRanked = (index, direction, key) => {
        moveProgram(index, direction);
        const target = index + direction;
        if (target === 0) refocusId.current = `ca-rank-${key}-down`;
        if (target === selectedPrograms.length - 1) {
            refocusId.current = `ca-rank-${key}-up`;
        }
    };

    return (
        <div>
            <h2 className="h5 mb-3">Select Programs</h2>
            <p className="text-muted mb-3">
                Choose which peer support programs you&apos;d like for this
                course, then rank them by preference. Programs review
                applications independently based on their own timelines.
            </p>
            <div
                id="ca-program-selection-group"
                // So a blocked submit can move focus here (#463)
                tabIndex={-1}
                role="group"
                aria-label="Select programs"
                aria-invalid={groupInvalid}
                aria-describedby={
                    groupInvalid
                        ? 'ca-program-selection-group-error'
                        : undefined
                }
                tabIndex={-1}
                className={
                    groupInvalid
                        ? 'border border-danger rounded p-2'
                        : undefined
                }
            >
                <Row className="mb-2">
                    {PROGRAMS.map((program) => {
                        const isSelected = selectedPrograms.some(
                            (p) => p.key === program.key
                        );
                        // The server's verdict for this viewer. Absent only
                        // while the term is loading or missing, when the
                        // submit check still refuses a closed program.
                        const verdict =
                            currentTerm?.applicationWindows?.[program.key];
                        const accepting = !verdict || verdict.accepting;
                        // A switched-off program says so in its own words and
                        // links to its page; the eligibility check leaves it
                        // out too, which would otherwise read as the course
                        // being the reason.
                        const switchedOff = isSwitchedOff(verdict);
                        const eligibleForCourse =
                            switchedOff ||
                            eligiblePrograms === null ||
                            eligiblePrograms.includes(program.key);
                        const eligible = eligibleForCourse && accepting;
                        // A selected program can always be removed, even
                        // one that's no longer eligible (e.g. copied by a
                        // duplicate, or its window closed)
                        const canToggle = eligible || isSelected;
                        const windowInfo =
                            verdict && !switchedOff
                                ? describeProgramWindow(verdict, now)
                                : null;
                        const descId = `program-desc-${program.key}`;
                        const windowId = `program-window-${program.key}`;
                        const ineligibleId = `program-ineligible-${program.key}`;
                        const comingSoonId = `program-coming-soon-${program.key}`;
                        const describedBy = [
                            descId,
                            windowInfo && windowId,
                            !eligibleForCourse && ineligibleId,
                            switchedOff && comingSoonId,
                        ]
                            .filter(Boolean)
                            .join(' ');
                        return (
                            <Col md={4} key={program.key} className="mb-3">
                                <Card
                                    className={`h-100 ${isSelected ? 'border-primary border-2' : ''} ${canToggle ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                                    onClick={() =>
                                        canToggle && toggleProgram(program)
                                    }
                                >
                                    <Card.Body className="text-center">
                                        {/* Link is a sibling, not nested, so
                                            axe doesn't flag two interactive
                                            controls in one. Link stops its
                                            click from bubbling to the Card.
                                            ApplicationDetailPage.js uses
                                            role="button" for the same control
                                            — no nested link there to split. */}
                                        <div
                                            role="checkbox"
                                            aria-checked={isSelected}
                                            aria-disabled={!canToggle}
                                            aria-label={`Select ${program.full}`}
                                            aria-describedby={describedBy}
                                            tabIndex={canToggle ? 0 : -1}
                                            onKeyDown={(e) => {
                                                if (
                                                    e.key !== 'Enter' &&
                                                    e.key !== ' '
                                                ) {
                                                    return;
                                                }
                                                e.preventDefault();
                                                if (canToggle) {
                                                    toggleProgram(program);
                                                }
                                            }}
                                        >
                                            {/* Only the program's own copy
                                                dims; the reason it cannot be
                                                chosen stays at full contrast. */}
                                            <div
                                                className={
                                                    eligible
                                                        ? undefined
                                                        : 'opacity-50'
                                                }
                                            >
                                                <Badge
                                                    bg={program.bg || ''}
                                                    text={badgeTextFor(
                                                        program.bg || ''
                                                    )}
                                                    className={`fs-6 px-3 py-2 mb-2 ${program.badgeClass || ''}`}
                                                >
                                                    {program.label}
                                                </Badge>
                                                <p className="fw-semibold mb-1">
                                                    {program.full}
                                                </p>
                                                <p
                                                    id={descId}
                                                    className="text-muted small mb-0"
                                                >
                                                    {program.description}
                                                </p>
                                            </div>
                                            {windowInfo && (
                                                <small
                                                    id={windowId}
                                                    className={`d-block mt-1 ${accepting ? 'text-muted' : 'text-danger'}`}
                                                >
                                                    {windowInfo.text}
                                                </small>
                                            )}
                                            {!eligibleForCourse && (
                                                <small
                                                    id={ineligibleId}
                                                    className="text-danger d-block mt-1"
                                                >
                                                    {unavailableReasons[
                                                        program.key
                                                    ] ||
                                                        'Not eligible for this course'}
                                                </small>
                                            )}
                                            {isSelected && (
                                                <div className="mt-2">
                                                    <Badge bg="primary" pill>
                                                        Selected
                                                    </Badge>
                                                </div>
                                            )}
                                        </div>
                                        {switchedOff && (
                                            <ProgramComingSoon
                                                programType={program.key}
                                                id={comingSoonId}
                                                className="mt-2"
                                            />
                                        )}
                                        <Link
                                            to={`/about#${program.key}`}
                                            className="small mt-1 d-inline-block"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`Learn more about ${program.full} (opens in new tab)`}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            Learn more
                                        </Link>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
                {groupInvalid && (
                    <p
                        id="ca-program-selection-group-error"
                        className="text-danger small mb-0"
                        role="alert"
                    >
                        Select at least one program.
                    </p>
                )}
            </div>

            {selectedPrograms.length > 0 && (
                <Card className="bg-light border-0">
                    <Card.Body>
                        <h3 className="h6">Your Ranked Preferences</h3>
                        <p className="text-muted small">
                            1 is your first choice. Use the arrows to reorder.
                        </p>
                        <ListGroup variant="flush">
                            {selectedPrograms.map((program, index) => (
                                <ListGroup.Item
                                    key={program.key}
                                    className="d-flex align-items-center justify-content-between gap-2 bg-transparent"
                                >
                                    <div className="d-flex align-items-center gap-2 gap-sm-3">
                                        <Badge
                                            bg="secondary"
                                            pill
                                            className="fs-6"
                                        >
                                            {program.rank}
                                        </Badge>
                                        <Badge
                                            bg={program.bg || ''}
                                            text={badgeTextFor(
                                                program.bg || ''
                                            )}
                                            className={`program-rank-badge ${program.badgeClass || ''}`}
                                        >
                                            {program.label}
                                        </Badge>
                                        <span className="text-break">
                                            {program.full}
                                        </span>
                                    </div>
                                    <div className="d-flex flex-column flex-sm-row flex-shrink-0 gap-2">
                                        <Button
                                            variant="outline-primary"
                                            id={`ca-rank-${program.key}-up`}
                                            disabled={index === 0}
                                            onClick={() =>
                                                moveRanked(
                                                    index,
                                                    -1,
                                                    program.key
                                                )
                                            }
                                            className="rank-move-btn"
                                            aria-label={`Move ${program.full} up`}
                                        >
                                            &#9650;
                                        </Button>
                                        <Button
                                            variant="outline-primary"
                                            id={`ca-rank-${program.key}-down`}
                                            disabled={
                                                index ===
                                                selectedPrograms.length - 1
                                            }
                                            onClick={() =>
                                                moveRanked(
                                                    index,
                                                    1,
                                                    program.key
                                                )
                                            }
                                            className="rank-move-btn"
                                            aria-label={`Move ${program.full} down`}
                                        >
                                            &#9660;
                                        </Button>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </Card.Body>
                </Card>
            )}

            {hasTA && (
                <Alert variant="info" className="mt-3">
                    <strong>TA Selected:</strong> You will need to provide at
                    least one named student (name, email) for the TA program.
                </Alert>
            )}
        </div>
    );
}

export default CreateApplicationSection2;
